import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import ExtractByTypename from 'graphql-client/ExtractByTypename';

import {FormActionForSchema} from './graphql/__generated/FormActionForSchema';
import {InitializeFormDesignerWithFormActionQuery} from './graphql/__generated/InitializeFormDesignerWithFormActionQuery';

type FormActionInvocationParameter = ExtractByTypename<
  FormActionForSchema['formAction'],
  'FormActionNode'
>['invocationParameters'][number];

type FormAction = ExtractByTypename<FormActionForSchema['formAction'], 'FormActionNode'>;

type ProcessTaskSchema =
  InitializeFormDesignerWithFormActionQuery['processTaskSchemasSupportingPlatformFeature'][number];

export {FormAction, FormActionInvocationParameter, ProcessTaskSchema};

// Keep in sync with benchling/forms/models/form_definition_content:FormFieldType
export enum FormFieldType {
  BOOLEAN = 'BOOLEAN',
  DATETIME = 'DATETIME',
  DATE = 'DATE',
  DECIMAL = 'DECIMAL',
  INTEGER = 'INTEGER',
  TEXT = 'TEXT',

  BIOENTITY = 'BIOENTITY',
  BASIC_FOLDER_ITEM = 'BASIC_FOLDER_ITEM',
  MIXTURE = 'MIXTURE',
  MOLECULE = 'MOLECULE',
  PROTEIN = 'PROTEIN',
  SEQUENCE = 'SEQUENCE',
  ENTRY = 'ENTRY',
  STORABLE = 'STORABLE',
  BOX = 'BOX',
  CONTAINER = 'CONTAINER',
  LOCATION = 'LOCATION',
  PLATE = 'PLATE',
  PROCESS = 'PROCESS',
  PROCESS_TASK = 'PROCESS_TASK',

  DROPDOWN_OPTION = 'DROPDOWN_OPTION',
}

/**
 * @returns Readable, user-facing text for a form field type
 */
export function formFieldTypeToReadableName(type: FormFieldType): string {
  switch (type) {
    case FormFieldType.BASIC_FOLDER_ITEM:
      return 'folder item';
    case FormFieldType.PROCESS_TASK:
      return 'workflow task';
    case FormFieldType.DROPDOWN_OPTION:
      return 'dropdown';
    default:
      return type.toLowerCase();
  }
}

export const FORM_FIELD_TYPE_TO_BNCH_TYPE_DEF: {[key in FormFieldType]: LeafType} = {
  [FormFieldType.BOOLEAN]: LeafType.BOOLEAN,
  [FormFieldType.DATETIME]: LeafType.DATETIME,
  [FormFieldType.DATE]: LeafType.DATE,
  [FormFieldType.DECIMAL]: LeafType.DECIMAL,
  [FormFieldType.INTEGER]: LeafType.INTEGER,
  [FormFieldType.TEXT]: LeafType.TEXT,

  [FormFieldType.BIOENTITY]: LeafType.BIOENTITY,
  [FormFieldType.BASIC_FOLDER_ITEM]: LeafType.BASIC_FOLDER_ITEM,
  [FormFieldType.MIXTURE]: LeafType.MIXTURE,
  [FormFieldType.MOLECULE]: LeafType.MOLECULE,
  [FormFieldType.PROTEIN]: LeafType.PROTEIN,
  [FormFieldType.SEQUENCE]: LeafType.SEQUENCE,
  [FormFieldType.ENTRY]: LeafType.ENTRY,
  [FormFieldType.STORABLE]: LeafType.STORABLE,
  [FormFieldType.BOX]: LeafType.BOX,
  [FormFieldType.CONTAINER]: LeafType.CONTAINER,
  [FormFieldType.LOCATION]: LeafType.LOCATION,
  [FormFieldType.PLATE]: LeafType.PLATE,
  [FormFieldType.PROCESS]: LeafType.PROCESS,
  [FormFieldType.PROCESS_TASK]: LeafType.PROCESS_TASK,

  [FormFieldType.DROPDOWN_OPTION]: LeafType.DROPDOWN_OPTION,
};

// Note: this is a non-exhaustive mapping!
export const BNCH_TYPE_DEF_TO_FORM_FIELD_TYPE: {[key in LeafType]?: FormFieldType} = {
  [LeafType.BOOLEAN]: FormFieldType.BOOLEAN,
  [LeafType.DATETIME]: FormFieldType.DATETIME,
  [LeafType.DATE]: FormFieldType.DATE,
  [LeafType.DECIMAL]: FormFieldType.DECIMAL,
  [LeafType.INTEGER]: FormFieldType.INTEGER,
  [LeafType.TEXT]: FormFieldType.TEXT,
  [LeafType.BIOENTITY]: FormFieldType.BIOENTITY,
  [LeafType.BASIC_FOLDER_ITEM]: FormFieldType.BASIC_FOLDER_ITEM,
  [LeafType.MIXTURE]: FormFieldType.MIXTURE,
  [LeafType.MOLECULE]: FormFieldType.MOLECULE,
  [LeafType.PROTEIN]: FormFieldType.PROTEIN,
  [LeafType.SEQUENCE]: FormFieldType.SEQUENCE,
  [LeafType.ENTRY]: FormFieldType.ENTRY,
  [LeafType.STORABLE]: FormFieldType.STORABLE,
  [LeafType.BOX]: FormFieldType.BOX,
  [LeafType.CONTAINER]: FormFieldType.CONTAINER,
  [LeafType.LOCATION]: FormFieldType.LOCATION,
  [LeafType.PLATE]: FormFieldType.PLATE,
  [LeafType.PROCESS]: FormFieldType.PROCESS,
  [LeafType.PROCESS_TASK]: FormFieldType.PROCESS_TASK,
  [LeafType.DROPDOWN_OPTION]: FormFieldType.DROPDOWN_OPTION,
};

export enum FormSectionType {
  STATIC = 'STATIC',
  ITERABLE = 'ITERABLE',
}

export const FormSectionTypeNameById = {
  [FormSectionType.STATIC]: 'Shared',
  [FormSectionType.ITERABLE]: 'Repeatable',
};

export interface FormDefinitionContent {
  sections: Array<FormDefinitionSection>;
}

export type uuid = string;

export interface FormDefinitionSection {
  type: 'FormSection';
  key: uuid;
  name: string;
  sectionType: FormSectionType;
  isIterable: boolean;
  isNameVisible: boolean;
  content: Array<FormDefinitionField | FormDefinitionSection>;
  description?: string;
}

export type FormDesignerSection = Omit<FormDefinitionSection, 'type' | 'content'>;

export interface FormDefinitionField {
  type: 'FormField';
  fieldType: FormFieldType;
  key: uuid;
  label: string;
  matchesSchemaFieldName: boolean;
  isRequired: boolean;
  description: string | null;

  // dropdown
  schemaFieldSelectorId?: string;

  // schematizable
  schemaId?: string;
}

export interface FormDesignerAction {
  key: uuid;
  selectedAction: Action | undefined;
  selectedSchema: ProcessTaskSchema | undefined;
  /** the section that this action is associated with (only needed for repeated actions) */
  sectionKey?: uuid;
  /** prevent editing of the action */
  lockAction?: boolean;
  /** prevent editing of the schema */
  lockSchema?: boolean;
  fields: Array<FormActionField>;
}

export interface FormActionField {
  key: uuid;
  /** whether field is visible in the form */
  included: boolean;
  /** whether field must be non-empty in the form */
  required: boolean;
  actionParameter: FormActionInvocationParameter;
  displayName: string;
  description: string | null;
  sectionKey: uuid;
}

export interface FormActionConfigInput {
  identifier: string;
  actionVersion: number;
  schemaId?: string;
  sectionKey?: string;
  parameters: Array<FormActionConfigInputParameter>;
}

export interface FormActionConfigInputParameter {
  formFieldKey: uuid | null;
  actionParameterId: string;
}

// ACTIONS
// these are temporary and will be replaced by more common shared types with the Action spec!
export interface Action {
  name: string;
  identifier: string;
  version: number;
  tags?: Array<string>;
  configurationParameters?: Array<ActionParameter>;
}

export interface ActionParameter {}

// Analytics
export const FORM_FIELD_SETTINGS_MODAL_TYPE = 'form_field_edit_modal';
